// Las pantallas intermedias después de pagar (la videollamada de Ultra y la oferta de
// facturación) — ADR-2026-10-06-pantallas-post-pago-renovadas. Mismo armado para las dos:
// en desktop, una tarjeta centrada sobre un fondo con las luces de la marca; en el teléfono,
// pantalla completa con las acciones abajo, donde llega el pulgar. Es sólo presentación:
// cada pantalla decide adónde va cada botón.

import type { ReactNode } from 'react';

export interface PuntoPostPago {
  icono: ReactNode;
  texto: string;
}

export interface AccionPostPago {
  label: string;
  /** Con `href` es un link (se abre en otra pestaña si `externa`); si no, un botón. */
  href?: string;
  externa?: boolean;
  onClick?: () => void;
  icono?: ReactNode;
}

export function PantallaPostPago({
  icono,
  insignia,
  titulo,
  texto,
  puntos,
  primaria,
  secundaria,
  nota,
}: {
  icono: ReactNode;
  insignia: string;
  titulo: string;
  texto: string;
  puntos: PuntoPostPago[];
  primaria: AccionPostPago;
  secundaria: AccionPostPago;
  /** Una línea chica debajo de las acciones (dónde encontrarlo después). */
  nota?: string;
}) {
  const clasePrimaria =
    'inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-brand-primary px-5 text-[15px] font-semibold text-white shadow-[0_14px_28px_-14px_rgba(22,68,235,0.75)] transition-colors hover:bg-brand-primary-hover';
  return (
    <div className="fixed inset-0 z-40 overflow-y-auto bg-[#F4F6FB]">
      {/* Las luces de la marca, de fondo: sólo decoran. */}
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-32 -top-40 h-[420px] w-[420px] rounded-full bg-[#60E7F0] opacity-30 blur-[90px]" />
        <div className="absolute -bottom-48 -left-40 h-[460px] w-[460px] rounded-full bg-[#1644EB] opacity-[0.16] blur-[100px]" />
      </div>

      <div className="relative flex min-h-full flex-col md:items-center md:justify-center md:px-6 md:py-12">
        <article className="flex min-h-[100dvh] w-full flex-col bg-white md:min-h-0 md:max-w-[460px] md:overflow-hidden md:rounded-[28px] md:border md:border-black/[0.06] md:shadow-[0_40px_80px_-40px_rgba(2,6,43,0.45)]">
          {/* Cabecera: el ícono sobre un degradé suave de la marca. */}
          <div className="relative flex flex-col items-center bg-gradient-to-b from-[#EEF2FF] to-white px-6 pb-6 pt-[calc(env(safe-area-inset-top,0px)+3rem)] md:pt-10">
            <span className="relative grid h-[72px] w-[72px] place-items-center rounded-[22px] bg-gradient-to-br from-[#2563F5] to-[#60E7F0] text-white shadow-[0_18px_36px_-14px_rgba(22,68,235,0.8)]">
              {icono}
            </span>
            <span className="mt-5 inline-flex items-center rounded-full bg-white px-3 py-1 text-[12px] font-semibold text-brand-primary shadow-sm ring-1 ring-[rgba(22,68,235,0.15)]">
              {insignia}
            </span>
          </div>

          <div className="flex flex-1 flex-col px-6 pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)] md:px-8 md:pb-8">
            <h1 className="text-balance text-center text-[24px] font-semibold leading-[1.15] tracking-[-0.01em] text-brand-black">
              {titulo}
            </h1>
            <p className="mx-auto mt-2.5 max-w-[360px] text-center text-[15px] leading-relaxed text-neutral-600">{texto}</p>

            <ul className="mt-6 flex flex-col gap-2.5">
              {puntos.map((p) => (
                <li key={p.texto} className="flex items-center gap-3 rounded-2xl bg-neutral-50 px-4 py-3 ring-1 ring-black/[0.04]">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-white text-brand-primary shadow-sm ring-1 ring-black/[0.05]">
                    {p.icono}
                  </span>
                  <span className="text-[14px] leading-snug text-neutral-800">{p.texto}</span>
                </li>
              ))}
            </ul>

            {/* En el teléfono las acciones van abajo de todo; en desktop, a continuación. */}
            <div className="mt-auto flex flex-col gap-2 pt-7">
              {primaria.href ? (
                <a
                  href={primaria.href}
                  target={primaria.externa ? '_blank' : undefined}
                  rel={primaria.externa ? 'noopener noreferrer' : undefined}
                  onClick={primaria.onClick}
                  className={clasePrimaria}
                >
                  {primaria.label} {primaria.icono}
                </a>
              ) : (
                <button type="button" onClick={primaria.onClick} className={clasePrimaria}>
                  {primaria.label} {primaria.icono}
                </button>
              )}
              <button
                type="button"
                onClick={secundaria.onClick}
                className="inline-flex h-11 w-full items-center justify-center rounded-xl px-5 text-[14.5px] font-medium text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-brand-black"
              >
                {secundaria.label}
              </button>
              {nota && <p className="mt-1 text-center text-[12.5px] leading-snug text-neutral-400">{nota}</p>}
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}

export default PantallaPostPago;
